import React from 'react';
import { variant } from 'styled-system';
import styled, { css } from 'styled-components/macro';

import {
  useTheme,
  themeGetCss,
  themeGetAsRem,
  themeGetResponsive,
  rem,
  useResponsiveCtx,
} from '@blueprint/ui';

import Close from './Close';
import ContentWrapper from './ContentWrapper';
import { Text } from '../Typography';
import Icon from '../Icon';
import Box from '../Box';

// Create Notification wrapper
const NotificationWrapper = styled(Box)`
  width: 100%;
  ${variant({ scale: 'notifications.variantMap' })}
  ${themeGetCss('notifications.base')}
  ${({ vMedia }) =>
    themeGetResponsive({
      vMedia,
      vMediaStyles: css`
        ${themeGetAsRem('notifications.sizeMapping.small')};
      `,
    })};

  padding: ${rem(15)} ${rem(8)} ${rem(15)} ${rem(20)};
  border-width: ${rem(1)};
`;

/**
 * Notification
 */
const Notification = React.forwardRef(
  // eslint-disable-next-line complexity
  (
    {
      title,
      children,
      variant: notificationVariant = 'system.error',
      component = 'div',
      closeButton,
      onClose,
      isRemovable = true,
      mode,
      inline,
      nowrap,
      closeButtonAriaLabel = 'close',
      fontVariant,
      iconTitleAccess,
      iconDescAccess,
      ...rest
    },
    ref,
  ) => {
    let notificationsIconVariantPath = `notifications.icon.variantMap.${notificationVariant}`;

    if (mode === 'dark' && notificationVariant.indexOf('inline') !== -1) {
      notificationsIconVariantPath = `notifications.icon.variantMap.inlineDark.${
        notificationVariant.split('.')[1]
      }`;
    }

    const iconThemeProps = useTheme(notificationsIconVariantPath);
    const { vMedia } = useResponsiveCtx();

    // get the close icon
    const close =
      closeButton && React.isValidElement(closeButton) ? (
        closeButton
      ) : (
        <Close
          component="button"
          data-testid="NotificationCloseButton"
          onClick={onClose}
          variant={notificationVariant}
          svgProps={{ color: 'inherit' }}
          aria-label={closeButtonAriaLabel}
        />
      );

    // should show notification inline
    const showInline =
      inline ??
      ((!React.isValidElement(children) &&
        React.Children.count(children) === 1) ||
        !children);

    const ariaTitle = notificationVariant && notificationVariant.split('.')[1];

    return (
      <NotificationWrapper
        // @ts-ignore
        as={component}
        display="inline-flex"
        alignItems="center"
        flexDirection="row"
        variant={notificationVariant}
        nowrap={nowrap}
        ref={ref}
        {...rest}
        vMedia={vMedia}
      >
        <Box as="span" display="inline-flex" width="100%">
          <Icon
            component="span"
            display="flex"
            alignSelf="baseline"
            titleAccess={iconTitleAccess || ariaTitle}
            descAccess={iconDescAccess}
            {...iconThemeProps}
          />

          <ContentWrapper
            show={!showInline && 'multiline'}
            variant={fontVariant || 'body1'}
            mode={mode}
            pr={
              notificationVariant.indexOf('inline') < 0
                ? isRemovable
                  ? rem(10)
                  : rem(30)
                : 0
            }
          >
            {React.isValidElement(title) ? (
              title
            ) : title ? (
              <Text
                variant="body1Title"
                display={showInline ? 'inline-flex' : 'block'}
                style={{ color: 'inherit' }}
                className="Notification-title"
                as="span"
              >
                {title}
              </Text>
            ) : null}

            {title && ' '}
            {children}
          </ContentWrapper>
          {isRemovable && close}
        </Box>
      </NotificationWrapper>
    );
  },
);

Notification.displayName = 'Notification';

export default Notification;
